import React, { useMemo, useState, Component } from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, MailIcon, ChevronRightIcon } from 'lucide-react';
const SALES_EMAIL = 'sales@sure-ai.tech';
const includedFeatures = ['Real-time passenger counting & upper-deck occupancy', 'Live wheelchair bay availability', 'Basic reporting & dashboard', 'API access for 3rd-party integration'];
type PricingOption = {
  id: 'standard' | 'subscription' | 'enterprise';
  label: string;
  price: string;
  priceUnit?: string;
  priceSuffix: string;
  extra?: string;
  extraSuffix?: string;
  note?: string;
  upfrontPerVehicle: number;
  monthlyPerVehicle: number;
};
const pricingOptions: PricingOption[] = [{
  id: 'standard',
  label: 'STANDARD',
  price: '£997',
  priceSuffix: 'upfront',
  extra: '+ £49.75',
  extraSuffix: 'per month',
  upfrontPerVehicle: 997,
  monthlyPerVehicle: 49.75
}, {
  id: 'subscription',
  label: 'SUBSCRIPTION',
  price: '£72.45',
  priceUnit: '/mo',
  priceSuffix: 'no upfront',
  note: 'Operating-cost model',
  upfrontPerVehicle: 0,
  monthlyPerVehicle: 72.45
}, {
  id: 'enterprise',
  label: 'ENTERPRISE',
  price: '£2,987',
  priceSuffix: 'upfront',
  note: 'All-in commercial pricing',
  upfrontPerVehicle: 2987,
  monthlyPerVehicle: 0
}];
type AddOn = {
  id: 'safety' | 'claims' | 'intelligence';
  number: string;
  tag: string;
  title: string;
  description: string;
  features: string[];
  builtFor: string;
  price: string;
  pricePerVehicle: number;
  highlight: boolean;
};
const addOns: AddOn[] = [{
  id: 'safety',
  number: '01',
  tag: 'HEALTH & SAFETY',
  title: 'Passenger Safety',
  description: 'Pre-built, off the shelf. Clear safety ROI. Aligns with DfT and TfL safety agendas.',
  features: ['Passenger falls detection', 'VAWG protection & prevention', 'Auto-archived footage & evidence', 'Unreported incidents & root cause'],
  builtFor: 'Health & Safety · Operations · Safety & Compliance',
  price: '£17.50',
  pricePerVehicle: 17.5,
  highlight: false
}, {
  id: 'claims',
  number: '02',
  tag: 'AUTOMATION & EVIDENCE',
  title: 'Claims & Risk Management',
  description: 'Directly reduces claims-handling cost and investigation time. Strong ROI, short payback.',
  features: ['AI natural-language smart search', 'Automated footage retrieval', 'Claims labelling & metadata', 'Incident event intelligence'],
  builtFor: 'Claims & Risk Management · CCTV Operations · Insurance & Legal',
  price: '£14.75',
  pricePerVehicle: 14.75,
  highlight: false
}, {
  id: 'intelligence',
  number: '03',
  tag: 'HIGHEST VALUE',
  title: 'Passenger Intelligence',
  description: 'Broader, higher-value package. Relevant across operator and authority audiences.',
  features: ['Passenger demographics', 'Upper & lower deck occupancy', 'Origin–destination analysis', 'Demand analytics & benchmarking', 'Ramp & wheelchair bay monitoring'],
  builtFor: 'Network Planning · Commercial · Revenue & BD · Advertising',
  price: '£27.50',
  pricePerVehicle: 27.5,
  highlight: true
}];
const formatGBP = (n: number) => new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
}).format(n);
export function Pricing() {
  const [selectedTier, setSelectedTier] = useState<PricingOption['id']>('subscription');
  const [selectedAddOns, setSelectedAddOns] = useState<AddOn['id'][]>(['safety']);
  const [vehicles, setVehicles] = useState<number>(50);
  const [company, setCompany] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const tier = pricingOptions.find(p => p.id === selectedTier)!;
  const chosenAddOns = addOns.filter(a => selectedAddOns.includes(a.id));
  const calculations = useMemo(() => {
    const v = Math.max(0, vehicles || 0);
    const tierUpfront = tier.upfrontPerVehicle * v;
    const tierMonthly = tier.monthlyPerVehicle * v;
    const addOnMonthly = chosenAddOns.reduce((sum, a) => sum + a.pricePerVehicle * v, 0);
    const totalMonthly = tierMonthly + addOnMonthly;
    const fiveYearTotal = tierUpfront + totalMonthly * 60;
    return {
      tierUpfront,
      tierMonthly,
      addOnMonthly,
      totalMonthly,
      fiveYearTotal
    };
  }, [tier, chosenAddOns, vehicles]);
  const toggleAddOn = (id: AddOn['id']) => {
    setSelectedAddOns(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const canSubmit = company.trim() && name.trim() && email.trim() && vehicles > 0;
  const buildEmail = () => {
    const subject = `SURE AI Quote Request — ${company} (${vehicles} vehicles)`;
    const lines = ['SURE AI — QUOTE REQUEST', '═══════════════════════════════════════════', '', 'CUSTOMER DETAILS', '───────────────────────────────────────────', `Company:       ${company}`, `Contact Name:  ${name}`, `Email:         ${email}`, phone ? `Phone:         ${phone}` : null, `Fleet Size:    ${vehicles} vehicle${vehicles === 1 ? '' : 's'}`, '', 'BASE PLATFORM', '───────────────────────────────────────────', `Tier:          ${tier.label}`, tier.upfrontPerVehicle > 0 ? `Upfront:       ${formatGBP(tier.upfrontPerVehicle)} / vehicle  =  ${formatGBP(calculations.tierUpfront)}` : `Upfront:       None`, tier.monthlyPerVehicle > 0 ? `Monthly:       ${formatGBP(tier.monthlyPerVehicle)} / vehicle  =  ${formatGBP(calculations.tierMonthly)} / month` : `Monthly:       Included in upfront`, '', 'ADD-ON PACKAGES', '───────────────────────────────────────────', ...(chosenAddOns.length === 0 ? ['(none selected)'] : chosenAddOns.map(a => `${a.number}  ${a.title.padEnd(28)} ${formatGBP(a.pricePerVehicle)} / veh / mo  =  ${formatGBP(a.pricePerVehicle * vehicles)} / month`)), '', 'QUOTE SUMMARY', '───────────────────────────────────────────', `One-off upfront:       ${formatGBP(calculations.tierUpfront)}`, `Base monthly:          ${formatGBP(calculations.tierMonthly)}`, `Add-ons monthly:       ${formatGBP(calculations.addOnMonthly)}`, `TOTAL MONTHLY:         ${formatGBP(calculations.totalMonthly)}`, `5-YEAR TOTAL (est.):   ${formatGBP(calculations.fiveYearTotal)}`, '', notes ? 'NOTES' : null, notes ? '───────────────────────────────────────────' : null, notes || null, notes ? '' : null, '───────────────────────────────────────────', 'All prices ex VAT · GBP · Based on 5-year subscription', 'Generated from sure-ai.tech/pricing'].filter(Boolean) as string[];
    return {
      subject,
      body: lines.join('\n')
    };
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    const {
      subject,
      body
    } = buildEmail();
    const mailto = `mailto:${SALES_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailto;
    setSubmitted(true);
  };
  return <>
      {/* Hero */}
      <section className="bg-sure-black w-full pt-40 pb-16 md:pb-20 border-b border-white/5" data-id="element-306">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12" data-id="element-307">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8" data-id="element-308">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            ease: [0.16, 1, 0.3, 1]
          }} className="max-w-4xl" data-id="element-309">
              <span className="font-sans text-sure-accent text-xs md:text-sm uppercase tracking-[0.22em] font-semibold mb-6 block" data-id="element-310">
                Build Your Quote
              </span>
              <h1 className="font-display text-sure-white text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.08] tracking-[-0.02em] mb-6" data-id="element-311">
                One platform. Modular design.{' '}
                <span className="text-sure-accent" data-id="element-312">
                  Choose what you deploy.
                </span>
              </h1>
              <p className="font-sans text-sure-gray text-base md:text-lg font-light leading-relaxed max-w-3xl" data-id="element-313">
                Select a base tier, pick the add-ons you need, enter your fleet
                size, and we'll send you a tailored quote within one business
                day.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="font-mono text-xs text-sure-gray/70 uppercase tracking-widest space-y-1 md:text-right shrink-0" data-id="element-314">
              <div data-id="element-315">GBP · EX VAT</div>
              <div data-id="element-316">Based on 5-year subscription</div>
              <div className="text-sure-accent" data-id="element-317">April 2026</div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="bg-sure-black w-full py-12 md:py-16" data-id="element-318">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12" data-id="element-319">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12" data-id="element-320">
            {/* Left: Selections */}
            <div className="lg:col-span-8 space-y-16" data-id="element-321">
              {/* Step 1: Base Platform */}
              <div data-id="element-322">
                <div className="flex items-center gap-3 mb-6" data-id="element-323">
                  <span className="font-mono text-sure-accent text-sm" data-id="element-324">01</span>
                  <span className="font-sans text-sure-accent text-[11px] uppercase tracking-[0.22em] font-semibold" data-id="element-325">
                    Base Platform · Select One
                  </span>
                </div>

                <div className="relative bg-sure-dark border-l-2 border-sure-accent rounded-sm overflow-hidden mb-6" data-id="element-326">
                  <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-sure-accent/5 rounded-full blur-[120px] pointer-events-none" data-id="element-327" />
                  <div className="relative p-6 md:p-8" data-id="element-328">
                    <h2 className="font-display text-sure-accent text-2xl md:text-3xl font-bold tracking-[-0.02em] leading-[1.1] mb-2" data-id="element-329">
                      SURE AI
                    </h2>
                    <p className="font-sans text-sure-white text-sm font-medium mb-4" data-id="element-330">
                      Edge hardware + software platform · Included with every
                      tier
                    </p>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2" data-id="element-331">
                      {includedFeatures.map(feature => <li key={feature} className="flex items-start gap-2 font-sans text-sure-gray text-xs leading-relaxed" data-id="element-332">
                          <CheckIcon className="w-3.5 h-3.5 mt-0.5 text-sure-accent shrink-0" data-id="element-333" />
                          {feature}
                        </li>)}
                    </ul>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-334">
                  {pricingOptions.map(option => {
                  const isSelected = selectedTier === option.id;
                  return <button key={option.id} type="button" onClick={() => setSelectedTier(option.id)} className={`text-left bg-sure-dark border rounded-sm p-5 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-sure-accent focus:ring-offset-2 focus:ring-offset-sure-black ${isSelected ? 'border-sure-accent bg-sure-accent/[0.03] shadow-[0_0_0_1px_#00FF87_inset]' : 'border-white/10 hover:border-white/30'}`} data-id="element-335">
                        <div className="flex items-center justify-between mb-4" data-id="element-336">
                          <div className="flex items-center gap-2" data-id="element-337">
                            <span className={`w-3 h-3 rounded-full border-2 transition-colors ${isSelected ? 'bg-sure-accent border-sure-accent' : 'border-white/30'}`} data-id="element-338" />
                            <span className="font-sans text-sure-white text-[11px] uppercase tracking-[0.22em] font-semibold" data-id="element-339">
                              {option.label}
                            </span>
                          </div>
                        </div>
                        <div className="font-display font-bold text-sure-white text-3xl tracking-[-0.02em] leading-none mb-1" data-id="element-340">
                          {option.price}
                          {option.priceUnit && <span className="font-mono font-normal text-sm text-sure-gray ml-1" data-id="element-341">
                              {option.priceUnit}
                            </span>}
                        </div>
                        <div className="font-sans text-sure-gray text-[10px] uppercase tracking-widest mb-3" data-id="element-342">
                          {option.priceSuffix}
                        </div>
                        {option.extra && <>
                            <div className="font-display font-semibold text-sure-white text-lg tracking-[-0.02em] mt-3" data-id="element-343">
                              {option.extra}
                            </div>
                            <div className="font-sans text-sure-gray text-[10px] uppercase tracking-widest" data-id="element-344">
                              {option.extraSuffix}
                            </div>
                          </>}
                        {option.note && <div className="font-sans text-sure-gray text-xs leading-relaxed mt-3" data-id="element-345">
                            {option.note}
                          </div>}
                      </button>;
                })}
                </div>
              </div>

              {/* Step 2: Add-Ons */}
              <div data-id="element-346">
                <div className="flex items-center justify-between mb-6" data-id="element-347">
                  <div className="flex items-center gap-3" data-id="element-348">
                    <span className="font-mono text-sure-accent text-sm" data-id="element-349">
                      02
                    </span>
                    <span className="font-sans text-sure-accent text-[11px] uppercase tracking-[0.22em] font-semibold" data-id="element-350">
                      Add-on Packages · Pick 1, 2 or 3
                    </span>
                  </div>
                  <span className="font-mono text-sure-gray text-xs" data-id="element-351">
                    {selectedAddOns.length} selected
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-352">
                  {addOns.map(addon => {
                  const isSelected = selectedAddOns.includes(addon.id);
                  return <button key={addon.id} type="button" onClick={() => toggleAddOn(addon.id)} className={`text-left relative bg-sure-dark border rounded-sm p-6 flex flex-col h-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-sure-accent focus:ring-offset-2 focus:ring-offset-sure-black ${isSelected ? 'border-sure-accent shadow-[0_0_0_1px_#00FF87_inset] bg-sure-accent/[0.03]' : addon.highlight ? 'border-sure-accent/30 hover:border-sure-accent/60' : 'border-white/10 hover:border-white/30'}`} data-id="element-353">
                        {/* Checkbox indicator */}
                        <div className="absolute top-4 right-4" data-id="element-354">
                          <div className={`w-5 h-5 rounded-sm border-2 flex items-center justify-center transition-colors ${isSelected ? 'bg-sure-accent border-sure-accent' : 'border-white/30'}`} data-id="element-355">
                            {isSelected && <CheckIcon className="w-3 h-3 text-sure-black" strokeWidth={3} data-id="element-356" />}
                          </div>
                        </div>

                        <div className="flex items-start gap-3 mb-4 pr-8" data-id="element-357">
                          <span className="font-mono text-sure-accent text-sm font-medium" data-id="element-358">
                            {addon.number}
                          </span>
                          <span className={`font-sans text-[10px] uppercase tracking-[0.22em] font-semibold ${addon.highlight ? 'text-sure-accent' : 'text-sure-gray'}`} data-id="element-359">
                            {addon.tag}
                          </span>
                        </div>

                        <h3 className={`font-display text-xl md:text-2xl font-bold tracking-[-0.02em] leading-[1.1] mb-3 ${addon.highlight ? 'text-sure-accent' : 'text-sure-white'}`} data-id="element-360">
                          {addon.title}
                        </h3>

                        <p className="font-sans text-sure-gray text-xs font-light leading-relaxed mb-4 pb-4 border-b border-white/10" data-id="element-361">
                          {addon.description}
                        </p>

                        <ul className="space-y-2 mb-6 flex-grow" data-id="element-362">
                          {addon.features.map(feature => <li key={feature} className="flex items-start gap-2 font-sans text-sure-white text-xs leading-relaxed" data-id="element-363">
                              <CheckIcon className="w-3 h-3 mt-0.5 text-sure-accent shrink-0" data-id="element-364" />
                              {feature}
                            </li>)}
                        </ul>

                        <div className="pt-4 border-t border-white/10 mb-4" data-id="element-365">
                          <span className="font-sans text-sure-white/60 text-[10px] uppercase tracking-[0.22em] font-semibold mb-2 block" data-id="element-366">
                            Built For
                          </span>
                          <p className="font-sans text-sure-gray text-[11px] leading-relaxed" data-id="element-367">
                            {addon.builtFor}
                          </p>
                        </div>

                        <div className="flex items-baseline gap-2" data-id="element-368">
                          <span className={`font-display text-2xl md:text-3xl font-bold tracking-[-0.02em] ${addon.highlight ? 'text-sure-accent' : 'text-sure-white'}`} data-id="element-369">
                            {addon.price}
                          </span>
                          <span className="font-sans text-sure-gray text-[10px] leading-tight" data-id="element-370">
                            / vehicle
                            <br data-id="element-371" />/ month
                          </span>
                        </div>
                      </button>;
                })}
                </div>
              </div>

              {/* Step 3: Fleet & Contact */}
              <div data-id="element-372">
                <div className="flex items-center gap-3 mb-6" data-id="element-373">
                  <span className="font-mono text-sure-accent text-sm" data-id="element-374">03</span>
                  <span className="font-sans text-sure-accent text-[11px] uppercase tracking-[0.22em] font-semibold" data-id="element-375">
                    Fleet & Contact Details
                  </span>
                </div>

                <div className="bg-sure-dark border border-white/10 rounded-sm p-6 md:p-8 space-y-6" data-id="element-376">
                  {/* Fleet Size */}
                  <div data-id="element-377">
                    <label className="font-sans text-sure-white/70 text-[11px] uppercase tracking-[0.22em] font-semibold mb-3 block" data-id="element-378">
                      Fleet Size · Number of Vehicles
                    </label>
                    <div className="flex items-center gap-4" data-id="element-379">
                      <input type="range" min={1} max={1000} step={1} value={vehicles} onChange={e => setVehicles(parseInt(e.target.value) || 0)} className="flex-1 accent-sure-accent h-1 bg-white/10 rounded-full appearance-none cursor-pointer" data-id="element-380" />
                      <input type="number" min={1} value={vehicles} onChange={e => setVehicles(parseInt(e.target.value) || 0)} className="w-28 bg-sure-black border border-white/10 rounded-sm px-4 py-2 font-mono text-sure-white text-lg text-right focus:outline-none focus:border-sure-accent transition-colors" data-id="element-381" />
                    </div>
                    <p className="font-sans text-sure-gray text-xs mt-2" data-id="element-382">
                      Minimum 50 vehicles for production deployment.
                    </p>
                  </div>

                  {/* Contact */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-383">
                    <Field label="Company" value={company} onChange={setCompany} required data-id="element-384" />
                    <Field label="Your Name" value={name} onChange={setName} required data-id="element-385" />
                    <Field label="Work Email" type="email" value={email} onChange={setEmail} required data-id="element-386" />
                    <Field label="Phone (optional)" type="tel" value={phone} onChange={setPhone} data-id="element-387" />
                  </div>

                  <div data-id="element-388">
                    <label className="font-sans text-sure-white/70 text-[11px] uppercase tracking-[0.22em] font-semibold mb-2 block" data-id="element-389">
                      Notes (optional)
                    </label>
                    <textarea rows={3} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Deployment timeline, integration questions, anything specific..." className="w-full bg-sure-black border border-white/10 rounded-sm px-4 py-3 font-sans text-sure-white text-sm placeholder:text-sure-gray/50 focus:outline-none focus:border-sure-accent transition-colors resize-none" data-id="element-390" />
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Live Summary */}
            <div className="lg:col-span-4" data-id="element-391">
              <div className="lg:sticky lg:top-28" data-id="element-392">
                <motion.div initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.6
              }} className="bg-sure-dark border-l-2 border-sure-accent rounded-sm overflow-hidden" data-id="element-393">
                  <div className="relative" data-id="element-394">
                    <div className="absolute top-0 right-0 w-[300px] h-[300px] bg-sure-accent/5 rounded-full blur-[100px] pointer-events-none" data-id="element-395" />

                    <div className="relative p-6 md:p-8" data-id="element-396">
                      <span className="font-sans text-sure-accent text-[11px] uppercase tracking-[0.22em] font-semibold mb-2 block" data-id="element-397">
                        Your Quote
                      </span>
                      <h3 className="font-display text-sure-white text-2xl font-bold tracking-[-0.02em] mb-6" data-id="element-398">
                        Live Summary
                      </h3>

                      {/* Tier line */}
                      <div className="space-y-3 pb-5 mb-5 border-b border-white/10" data-id="element-399">
                        <div className="flex justify-between items-baseline" data-id="element-400">
                          <span className="font-sans text-sure-gray text-xs uppercase tracking-widest" data-id="element-401">
                            {tier.label}
                          </span>
                          <span className="font-mono text-sure-white text-xs" data-id="element-402">
                            × {vehicles}
                          </span>
                        </div>
                        {calculations.tierUpfront > 0 && <Row label="Upfront" value={formatGBP(calculations.tierUpfront)} data-id="element-403" />}
                        {calculations.tierMonthly > 0 && <Row label="Monthly" value={`${formatGBP(calculations.tierMonthly)} / mo`} data-id="element-404" />}
                      </div>

                      {/* Add-ons */}
                      <div className="space-y-3 pb-5 mb-5 border-b border-white/10" data-id="element-405">
                        <div className="font-sans text-sure-gray text-xs uppercase tracking-widest mb-2" data-id="element-406">
                          Add-Ons ({chosenAddOns.length})
                        </div>
                        {chosenAddOns.length === 0 ? <div className="font-sans text-sure-gray/60 text-xs italic" data-id="element-407">
                            None selected
                          </div> : chosenAddOns.map(a => <Row key={a.id} label={a.title} value={`${formatGBP(a.pricePerVehicle * vehicles)} / mo`} data-id="element-408" />)}
                      </div>

                      {/* Totals */}
                      <div className="space-y-3 pb-6 mb-6 border-b border-white/10" data-id="element-409">
                        <Row label="One-off upfront" value={formatGBP(calculations.tierUpfront)} emphasis data-id="element-410" />
                        <Row label="Total monthly" value={`${formatGBP(calculations.totalMonthly)} / mo`} emphasis data-id="element-411" />
                      </div>

                      {/* 5 Year */}
                      <div className="mb-6" data-id="element-412">
                        <div className="font-sans text-sure-accent text-[11px] uppercase tracking-[0.22em] font-semibold mb-2" data-id="element-413">
                          5-Year Total · Est.
                        </div>
                        <div className="font-display text-sure-accent text-4xl font-bold tracking-[-0.02em] leading-none" data-id="element-414">
                          {formatGBP(calculations.fiveYearTotal)}
                        </div>
                      </div>

                      {/* Submit */}
                      <form onSubmit={handleSubmit} data-id="element-415">
                        <button type="submit" disabled={!canSubmit} className="w-full px-6 py-4 bg-sure-accent text-sure-black text-sm uppercase tracking-widest font-semibold hover:bg-sure-accent/90 hover:scale-[1.01] transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-sure-accent focus:ring-offset-2 focus:ring-offset-sure-black disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100 inline-flex items-center justify-center gap-2" data-id="element-416">
                          <MailIcon className="w-4 h-4" data-id="element-417" />
                          Send Quote Request
                          <ChevronRightIcon className="w-4 h-4" data-id="element-418" />
                        </button>

                        {!canSubmit && <p className="font-sans text-sure-gray text-xs mt-3 text-center" data-id="element-419">
                            Fill in company, name, email & fleet size to send.
                          </p>}

                        {submitted && canSubmit && <p className="font-sans text-sure-accent text-xs mt-3 text-center" data-id="element-420">
                            ✓ Your email client opened with the quote
                            pre-filled.
                          </p>}

                        <p className="font-mono text-sure-gray/60 text-[10px] uppercase tracking-widest text-center mt-4" data-id="element-421">
                          Sends to {SALES_EMAIL}
                        </p>
                      </form>
                    </div>
                  </div>
                </motion.div>

                <p className="font-mono text-sure-gray/70 text-[10px] uppercase tracking-widest text-center mt-6" data-id="element-422">
                  All prices ex VAT · GBP · 5-year subscription
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>;
}
function Field({
  label,
  value,
  onChange,
  type = 'text',
  required = false
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
}) {
  return <div data-id="element-423">
      <label className="font-sans text-sure-white/70 text-[11px] uppercase tracking-[0.22em] font-semibold mb-2 block" data-id="element-424">
        {label} {required && <span className="text-sure-accent" data-id="element-425">*</span>}
      </label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} required={required} className="w-full bg-sure-black border border-white/10 rounded-sm px-4 py-2.5 font-sans text-sure-white text-sm focus:outline-none focus:border-sure-accent transition-colors" data-id="element-426" />
    </div>;
}
function Row({
  label,
  value,
  emphasis = false
}: {
  label: string;
  value: string;
  emphasis?: boolean;
}) {
  return <div className="flex justify-between items-baseline gap-3" data-id="element-427">
      <span className={`font-sans text-xs ${emphasis ? 'text-sure-white font-medium' : 'text-sure-gray'}`} data-id="element-428">
        {label}
      </span>
      <span className={`font-mono text-right ${emphasis ? 'text-sure-white text-base font-semibold' : 'text-sure-white/80 text-sm'}`} data-id="element-429">
        {value}
      </span>
    </div>;
}